import {
  useState,
  useEffect,
  useRef,
  createContext,
  useContext,
  ReactNode,
} from 'react';
import { getProjectDetail } from '@/services/project';
import { ProjectData } from '@/types/project';
import { useParams, useHybirdWallet, useToast, useFm } from '@web3/base-hooks';
import { useProjectsShare } from '@/hooks/useProjectsShare';
import { allowedWalletTypes } from '@/constants';
import dayjs from 'dayjs';
import { SwitchModal } from '@/components/SwitchModal';
import { useScreenType } from '@/hooks/media';
import { isApp, newTriggerOneLink } from '@web3/base-utils';
import { scTrack, WEB3_EVENT } from '@web3/base-tracking';
import { useAppVersion } from '@/hooks/appVersion';
import { useUserStatus } from '@/hooks/useUserStatus';
import { LoginStatus, UserStatus } from '@/types/user';
import { useRegisterModal } from '@/hooks/useRegisterModal';
import { Page } from '@/config/track-config';

interface Props {
  children: ReactNode;
}

const ProjectContext = createContext<{
  projectData: Partial<ProjectData>;
  isFinish: boolean;
  isError: boolean;
  checkUser: (params?: {
    url?: string;
    appVersionCheckLogin?: boolean;
  }) => Promise<boolean>;
  refresh: () => Promise<void>;
  isUnavailable: boolean;
  isNotStart: boolean;
  isEnd: boolean;
}>({
  projectData: {} as ProjectData,
  isFinish: false,
  isError: false,
  checkUser: () => Promise.resolve(true),
  refresh: () => undefined,
  isUnavailable: true,
  isNotStart: true,
  isEnd: true,
});

ProjectContext.displayName = 'ProjectContext';

export const ProjectProvider = (props: Props) => {
  const { children } = props;
  const [data, setData] = useState<Partial<ProjectData>>({});
  const [isFinish, setIsFnish] = useState(false);
  const [isError, setIsError] = useState(false);
  const [showSwitchModal, setShowSwitchModal] = useState(false);
  const { walletId, walletType, walletIsReady, userInfo } = useHybirdWallet();
  const { userStatus, userCommonParams } = useUserStatus();
  const { checkStatusAndRegister } = useRegisterModal();
  const { isMobile } = useScreenType();
  const isTrack = useRef(false);
  const fm = useFm();

  const { toastWarn } = useToast();

  const projectName = useParams().name;
  const activityCode = useParams().activityCode;

  const fetchData = async () => {
    if (!projectName) return;

    const { result, success, code } = await getProjectDetail({
      activityCode,
      name: projectName,
      ...userCommonParams,
    });

    if (!success) {
      let message = fm('systemError');

      if (code === 34003) {
        message = 'The current id does not exist';
      }
      toastWarn(message);
      setIsError(true);

      return;
    }

    setIsFnish(true);

    setData(result);
  };

  const checkUser = async ({ url = '', appVersionCheckLogin = false } = {}) => {
    if (isMobile && !isApp()) {
      const webUrl = window.location.href || '';

      newTriggerOneLink(url || webUrl);
      return false;
    }

    if ([UserStatus.NOT_READY].includes(userStatus)) {
      checkStatusAndRegister();
      return false;
    }

    return true;
  };

  const currentTime = dayjs.utc();

  const isNotStart = currentTime.isBefore(dayjs(data.start));
  const isEnd = currentTime.isAfter(dayjs(data.end));

  useEffect(() => {
    if (!projectName) {
      toastWarn('projectName is invalid');
    }

    if (!userCommonParams) return;

    fetchData();
  }, [userCommonParams]);

  useEffect(() => {
    if (!isFinish) return;
    if (isTrack.current) return;

    scTrack(WEB3_EVENT.Web3PageView, {
      page_name: Page.DeailPage,
      spmName: data.name,
    });

    isTrack.current = true;
  }, [data]);

  return (
    <ProjectContext.Provider
      value={{
        projectData: data,
        isFinish,
        isError,
        refresh: () => {
          return fetchData();
        },
        checkUser,
        isUnavailable: isNotStart || isEnd,
        isNotStart,
        isEnd,
      }}
    >
      {children}
      <SwitchModal
        isShow={showSwitchModal}
        setIsShow={(v) => {
          setShowSwitchModal(v);
        }}
      />
    </ProjectContext.Provider>
  );
};

export const useProjectData = () => {
  return useContext(ProjectContext);
};

export const useShare = () => {
  const { projectData } = useProjectData();
  const { showShare } = useProjectsShare({
    name: projectData.name,
    icon: projectData.icon,
    chainList: projectData.chains,
    track: projectData.track,
    points: projectData.point,
    supporters: projectData.joinUserCount,
    projectRank: projectData.rank,
    time: dayjs().local().format('YYYY-MM-DD HH:mm:ss'),
  });

  return {
    showShare,
  };
};
